<template>
  <right-side-bar
    fixed
    divider
    prepend
    width="30%"
    header-style="mt-2 mr-2"
    title="Add Account"
    @toggle-right-bar="toogleDrawer"
    :is-right-drawer-open="isDrawerOpen"
  >
    <v-form
      ref="userAccountForm"
      id="add-user-account-form"
      v-model="isFormValid"
    >
      <div class="form-group">
        <label class="overline font-weight-large" for="user-accounts-access">
          Account Access
        </label>
        <v-autocomplete
          outlined
          multiple
          small-chips
          item-text="name"
          label="Account"
          ref="accountsSelect"
          :items="accountsList"
          class="required pt-5"
          item-value="account_id"
          id="user-accounts-access"
          return-object
          clearable
          open-on-clear
          :loading="areAccountsLoading"
          :search-input.sync="searchAccount"
          v-model="formData.accounts"
          :rules="[requiredMultiple('Account')]"
          :menu-props="{ bottom: true, offsetY: true }"
          :no-data-text="$dictionary.app.common.accounts.noData"
        >
          <template #prepend-item>
            <v-list-item ripple @click="selectAllAccounts">
              <v-list-item-action class="mr-3">
                <v-icon :color="formData.accounts.length > 0 ? 'primary' : ''">
                  {{ allAccountsSelectedIcon }}
                </v-icon>
              </v-list-item-action>
              <v-list-item-content>
                <v-list-item-title>
                  Select All
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-divider class="mt-2"></v-divider>
          </template>
          <template #selection="{ item, attrs, index, selected, select }">
            <v-chip
              v-if="index < tooltipAccountIndex"
              class="filter-light-green-chip body-2 text--primary"
              v-bind="attrs"
              :input-value="selected"
              close
              @click="select"
              @click:close="removeAccount(item)"
            >
              {{ item.name }}
            </v-chip>
            <tooltip
              content-class="white-space-preline"
              #default="{ on, attrs }"
              :name="remainingAccountsTooltip"
              :type="$appConfig.tooltip.type.dark"
            >
              <span
                v-bind="attrs"
                v-on="on"
                v-if="index === tooltipAccountIndex"
                class="dark-grey caption cursor-pointer"
              >
                (+{{ formData.accounts.length - tooltipAccountIndex }}
                others)
              </span>
            </tooltip>
          </template>
          <template #item="{ on, attrs, item }">
            <v-list-item v-on="on" v-bind="attrs" #default="{ active }">
              <v-list-item-action class="mr-4">
                <v-checkbox :input-value="active" />
              </v-list-item-action>
              <v-list-item-content class="body-2 text--primary">
                {{ item.name }}
                <v-spacer />
              </v-list-item-content>
            </v-list-item>
          </template>
        </v-autocomplete>
      </div>
      <div class="form-group" id="add-user-account-roles">
        <v-radio-group
          mandatory
          class="mt-0"
          hide-details="auto"
          v-model="formData.role"
        >
          <template v-for="{ name, description } in rolesList">
            <div class="user-account-role mb-4" :key="name">
              <v-radio :label="name">
                <template #label>
                  <span class="text--primary" v-text="name" />
                </template>
              </v-radio>
              <div class="body-2 text--secondary pl-8">{{ description }}</div>
            </div>
          </template>
        </v-radio-group>
      </div>
      <div class="form-group">
        <v-divider class="mt-4 mb-6 border-light-grey" />
        <v-btn
          type="button"
          color="primary"
          :loading="isLoading"
          :disabled="!isFormValid"
          @click="onSubmitHandler"
          id="add-account-access-submit-btn"
          class="px-4 button font-weight-bold"
        >
          Save
        </v-btn>
      </div>
    </v-form>
  </right-side-bar>
</template>

<script>
import debounce from "lodash/debounce";
import { Tooltip } from "@/components/shared";

import { mapActions, mapGetters } from "vuex";
import RightSideBar from "./RightSideBar.vue";
import { requiredMultiple } from "@/validators";

import {
  ROLES,
  SORTING_ORDERS,
  DEBOUNCE_MILLIS,
  TOOLTIP_SEPERATOR,
} from "@/constants";
import { defer, getPropArr, removeArrayItem } from "@/utils/common.utils";
import { SharedUtilsMixin } from "@/mixins";

/**
 * Add account access to user drawer
 */
export default {
  name: "AddUserAccountDrawer",
  /**
  |--------------------------------------------------
  | Mixins
  |--------------------------------------------------
  */
  mixins: [SharedUtilsMixin],
  /**
   * ---------------- Props ------------------
   */
  props: {
    isDrawerOpen: { type: Boolean, default: true },
    userDetails: { type: Object, default: () => ({}), required: true },
    isLoading: { type: Boolean, default: false, required: true },
    /**
     * Actions to be perforomed for user account access
     */
    actions: { type: Object, required: true },
  },
  /**
   * ---------------- Data Properties ------------------
   */
  data() {
    return {
      tooltipAccountIndex: 5,
      areAccountsLoading: false,
      searchAccount: null,
      accountsList: [],
      formData: { accounts: [], role: [] },
      isFormValid: false,
    };
  },
  /**
   * ---------------- Components ------------------
   */
  components: {
    Tooltip,
    RightSideBar,
  },
  /**
   * ---------------- Computed Properties ------------------
   */
  computed: {
    ...mapGetters({
      accounts: "account/accounts",
      roles: "app/rolesCapitalized",
    }),
    removeArrayItem,
    requiredMultiple,
    /**
     * Computed roles list with description of the roles
     */
    rolesList() {
      // Roles descripton message
      const [adminDesc, userDesc] = [
        "Admins can manage the entire account, including the users.",
        "User can view and download reports for the selected accounts.",
      ];

      return this.roles.map((role) => ({
        name: role,
        description: role?.toLowerCase() === ROLES.admin ? adminDesc : userDesc,
      }));
    },
    /**
     * Accounts get api request params
     * @type {Object}
     */
    accountsParams() {
      return {
        sort_by: "name",
        sort_order: SORTING_ORDERS.asc,
        per_page: 20,
        page: 1,
      };
    },
    /**
     * Computes remaining selected accounts tooltip
     */
    remainingAccountsTooltip() {
      const { accounts } = this.formData;
      return accounts.length >= this.tooltipAccountIndex
        ? this.getAccountsTooltip()
        : "";
    },
    /**
     * Id of the user to be updated
     */
    userId() {
      return this.$route.params.id;
    },
    /**
     * Checks if all the accounts are selected in accounts dropdown
     * @type {Boolean}
     */
    allAccountsSelected() {
      return this.formData.accounts.length === this.accountsList.length;
    },
    /**
     * Select all option icon in accounts dropdown
     * @type {String}
     */
    allAccountsSelectedIcon() {
      if (this.allAccountsSelected) return "mdi-close-box";
      if (this.allAccountsSelected) return "mdi-minus-box";
      return "mdi-checkbox-blank-outline";
    },
    /**
     * Post api payload to add access to bulk accounts to the user
     */
    payload() {
      const { accounts, role } = this.formData;

      return {
        memberships: accounts.map(({ id }) => ({
          role: this.rolesList[role]?.name?.toLowerCase(),
          account_id: id,
        })),
      };
    },
  },
  /**
   * ---------------- Methods ------------------
   */
  methods: {
    /**
     * Maps store actions in the component
     */
    ...mapActions({
      getAccounts: "account/getAccounts",
      setSnackbar: "ui/setSnackbar",
    }),
    /**
     * Selects all accounts in accounts dropdown
     */
    selectAllAccounts() {
      if (this.allAccountsSelected) this.formData.accounts = [];
      else this.formData.accounts = this.accountsList.slice();
    },
    /**
     * Remove selected accounts on the click of close icon in chips at accounts autocomplete component
     * @param account_id Id of acccount to be deleted
     */
    removeAccount({ account_id }) {
      removeArrayItem(account_id, "account_id", this.formData.accounts);
    },
    /**
     * Toogles drawer
     * @emits toggle-drawer
     */
    toogleDrawer(event) {
      this.$emit("toggle-drawer", event);
    },
    /**
     * Submit handler
     */
    async onSubmitHandler() {
      if (!this.isFormValid) return this.$refs.userAccountForm.validate();

      const payload = [this.userId, this.payload, this.actions.add];
      this.$emit("update-user-accounts", ...payload);
      // Closes drawer
      defer(() => {
        this.toogleDrawer(false);
        this.$refs.userAccountForm.reset();
      }, 500);
    },
    /**
     * Selected remaining accounts tooltip
     * @returns {String} tooltip string
     */
    getAccountsTooltip() {
      // Remove first 5 elements if there are more than 5 elements in an array
      const remainingRecords = [...this.formData.accounts].splice(
        this.tooltipAccountIndex,
        this.formData.accounts.length - 1
      );
      return getPropArr(remainingRecords, "name").join(TOOLTIP_SEPERATOR);
    },
    /**
     * Fetches list of accounts
     * @param {Object} params Accounts api request params
     */
    async getAccountsList(params = this.accountsParams) {
      try {
        this.areAccountsLoading = true;
        await this.getAccounts(params);

        this.accountsList = [...this.accountsList, ...this.accounts];
      } catch (error) {
        this.setSnackbar({
          value: true,
          message: this.$errorMessage(error),
          type: this.snackbarTypes.error,
        });
      } finally {
        this.areAccountsLoading = false;
      }
    },
    /**
     * Debounce function to fetch accounts list with 500 millis delay
     */
    delayAccountsFetch: debounce(function() {
      const params = {
        ...this.accountsParams,
        "q[name_cont]": this.searchAccount,
      };
      this.getAccountsList(params);
    }, DEBOUNCE_MILLIS),
  },
  /**
   * ---------------- Watching properties ------------------
   */
  watch: {
    searchAccount(accountName) {
      this.delayAccountsFetch(accountName);
    },
  },
  /**
   * Created lifecycle hook
   */
  created() {
    this.getAccountsList();
  },
};
</script>
